











































































































































































































































import LazyHydrate from 'vue-lazy-hydration';
import {
  SfSelect,
  SfHeading,
  SfBreadcrumbs,
  SfAccordion,
  SfList,
  SfMenuItem,
  SfIcon
} from '@storefront-ui/vue';
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useFetch,
  useRoute,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { usePageStore } from '~/stores/page';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import CategoryPagination from '~/modules/catalog/category/components/pagination/CategoryPagination.vue';
import {
  useCategory,
  useFacet,
  useProduct,
  useUiHelpers,
  useUiState,
} from '~/composables';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import { usePrice } from '~/modules/catalog/pricing/usePrice';
import { useCategoryContent } from '~/modules/catalog/category/components/cms/useCategoryContent';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import facetGetters from '~/modules/catalog/category/getters/facetGetters';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import CategoryNavbar from '~/modules/catalog/category/components/navbar/CategoryNavbar.vue';
import CategoryBreadcrumbs from '~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue';
import type { CategoryTree, ProductInterface, Products } from '~/modules/GraphQL/types';
import type { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import type { Pagination } from '~/composables/types';
import type { Product } from '~/modules/catalog/product/types';
import { Breadcrumb } from '~/modules/catalog/types';

export default defineComponent({
  name: 'new-products',
  components: {
    CategoryPagination,
    CategoryEmptyResults: () => import('~/modules/catalog/category/components/CategoryEmptyResults.vue'),
    CategoryFilters: () => import('~/modules/catalog/category/components/filters/CategoryFilters.vue'),
    CmsContent: () => import('~/modules/catalog/category/components/cms/CmsContent.vue'),
    Banner: () => import('~/modules/catalog/category/components/header/Banner.vue'),
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryNavbar,
    CategoryBreadcrumbs,
    SfSelect,
    LazyHydrate,
    SfHeading,
    SkeletonLoader,
    SfBreadcrumbs,
    SfAccordion,
    SfMenuItem,
    SfList,
    SfIcon
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { localePath, app: { i18n } } = useContext();
    const { addTags } = useCache();
    const {
      toggleFilterSidebar,
      changeToCategoryListView,
      changeToCategoryGridView,
      isCategoryGridView,
      isFilterSidebarOpen,
    } = useUiState();
    const {
      load: loadWishlist,
      addItem: addItemToWishlistBase,
      isInWishlist,
      removeItem: removeItemFromWishlist,
    } = useWishlist();
    const { result, searchNewProducts } = useFacet();
    const { addItemToCart } = useAddToCart();
    const { activeCategory, loadCategoryTree } = useTraverseCategory();
    const { categories: categoryList, load: categoriesListLoad } = useCategory();
    const uiHelpers = useUiHelpers();
    const cmsContent = ref('');
    const isShowCms = ref(false);
    const isShowCategoryListOnMobile = ref(false);
    const isShowProducts = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const sortBy = ref<SortingModel>({ selected: '', options: [] });
    const pagination = ref<Pagination>({});
    const productContainerElement = ref<HTMLElement | null>(null);
    const activeCategoryName = computed(() => activeCategory.value?.name ?? '');
    const activeCategoryImage = computed(() => activeCategory.value?.image ?? '');
    const categoryTree = ref<CategoryTree[]>([]);
    const featuredCategories = ref<CategoryTree[]>([]);
    const isPriceLoaded = ref(false);  
    const categoryMeta = ref([]);
    const route = useRoute();

    const addItemToWishlist = async (product: Product) => {
      await (isInWishlist({ product })
        ? removeItemFromWishlist({ product })
        : addItemToWishlistBase({ product }));
    };
    
    let pageTitle = i18n.t('Products') as string;

    if ('new-products' == route.value.name) {
      pageTitle = i18n.t('New Products') as string;
    } else if ('big-deal-products' == route.value.name) {
      pageTitle = i18n.t('Big deal Products') as string;
    }

    const breadcrumbs = ref<Breadcrumb[]>([
      {
        text: i18n.t('Home') as string,
        link: localePath('/'),
      },
      {
        text: i18n.t('Search results') as string,
        link: localePath(route.value.path),
      },
    ]);
    

    const doClearFilters = () => {};
    let categoryUid = ref('');

    const updateCategoryId = async (uid: String) => {
      categoryUid.value = uid.toString();      
      
      if (!categoryUid) {
        return;
      }

      const [content, categoryMetaData] = await Promise.all([
          getContentData(categoryUid.value as string),
          loadCategoryMeta({ category_uid: categoryUid.value }),
          searchNewProducts({ ...uiHelpers.getFacetsFromURL(),  category_uid: categoryUid.value})
      ]);
      
      categoryMeta.value[0] = categoryMetaData;
      cmsContent.value = content?.cmsBlock?.content ?? '';
      isShowCms.value = content.isShowCms;
      isShowProducts.value = content.isShowProducts;

      processResult();
    }    

    const { fetch } = useFetch(async () => {
      if (!activeCategory.value) {
          await loadCategoryTree();
      }

      if (!categoryUid) {
        return;
      }

      const [content, categoryMetaData] = await Promise.all([
          getContentData(categoryUid.value as string),
          loadCategoryMeta({ category_uid: categoryUid.value }),
          searchNewProducts({ ...uiHelpers.getFacetsFromURL(),  category_uid: categoryUid.value})
      ]);

      categoryMeta.value[0] = categoryMetaData;
      cmsContent.value = content?.cmsBlock?.content ?? '';
      isShowCms.value = content.isShowCms;
      isShowProducts.value = content.isShowProducts;

      processResult();
    });


    useFetch(async () => {
      await categoriesListLoad({ pageSize: 20 });

      categoryTree.value = categoryList.value?.[0]?.children;
      
      categoryList.value?.[0]?.children.forEach((category) =>{
        getIncludedInMenuCategories(category)
      })

      await searchNewProducts({ ...uiHelpers.getFacetsFromURL(),  category_uid: categoryUid.value});

      processResult();
    });

    function getIncludedInMenuCategories(category: CategoryTree) {
      const categoryItem = JSON.parse(JSON.stringify(category));

      const children = categoryItem.children;
      delete categoryItem.children; // Removing unwanted data.
      
      if(categoryItem.include_in_menu) {
       featuredCategories.value.push(categoryItem);
      }
      
      if(children) {
        children.forEach((child) => {
          getIncludedInMenuCategories(child)
        });
      }
    }

    onMounted(async () => {
      loadWishlist();
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)?.price_range;
          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }
          return { ...product };
        });
      }
      isPriceLoaded.value = true;
    });

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);
      fetch();

      if (window?.scroll) {
        window.scroll(0, 0);
      }
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(0);
    };

    const onReloadProducts = () => {
      goToPage(0);
      isShowCategoryListOnMobile.value = false;
      // productContainerElement.value.scrollIntoView();
    };

    const processResult = () => {
      products.value = facetGetters.getProducts(result.value) ?? [];
      sortBy.value = facetGetters.getSortOptions(result.value);
      pagination.value = facetGetters.getPagination(result.value);

      const tags = [{ prefix: CacheTagPrefix.View, value: categoryUid.value }];
      const productTags = products.value.map((product) => ({
          prefix: CacheTagPrefix.Product,
          value: product.uid,
      }));

      addTags([...tags, ...productTags]);
    };

    const resetCategory = async () => {
      categoryUid.value = '';
      categoryMeta.value = [];
      isShowCategoryListOnMobile.value = false;
      await searchNewProducts({ ...uiHelpers.getFacetsFromURL(),  category_uid: categoryUid.value});
      processResult();
    };

    const toggleCategoryListOnMobile = () =>{
      isShowCategoryListOnMobile.value = !isShowCategoryListOnMobile.value;
    }

    return {
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      isCategoryGridView,
      changeToCategoryListView,
      changeToCategoryGridView,
      isFilterSidebarOpen,
      addItemToCart,
      addItemToWishlist,
      pagination,
      products,
      sortBy,
      isShowCms,
      isShowCategoryListOnMobile,
      isShowProducts,
      cmsContent,
      activeCategoryName,
      activeCategoryImage,
      pageTitle,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      categoryUid,
      breadcrumbs,
      categoryTree,
      updateCategoryId,
      toggleCategoryListOnMobile,
      doClearFilters,
      resetCategory
    };
  },
  head() {
    return getMetaInfo(this.categoryMeta);
  },
});
